import { useToastError } from "@/components/ui/use-toast";
import { useUser } from "@/contexts/user-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { companyRefusalKey } from "@/lib/company-refusal";
import {
  CompanyDTO,
  EmployeeDTO,
  EmployeeSearchResponse,
  TargetMatch,
} from "@shaarp/common";
import { useCallback, useEffect, useState } from "react";

const MAX_PAGES = 2;
const SMALL_COMPANY_THRESHOLD = 10;

type SmallCompanyDetection =
  | { mode: "exact"; threshold?: number }
  | { mode: "range"; value: string };

type UseEmployeeSearchOptions = {
  company: CompanyDTO;
  smallCompanyDetection?: SmallCompanyDetection;
};

export type UseEmployeeSearchReturn = {
  employees: EmployeeDTO[];
  error: Error | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isSearchQueryLoading: boolean;
  isSearching: boolean;
  hasSearched: boolean;
  isGeneratingQuery: boolean;
  targetMatch: TargetMatch | null;
  isSmallCompany: boolean;
  queryHasChanged: boolean;
  isSearchMoreMode: boolean;
  maxPagesReached: boolean;
  currentPage: number;
  hasMoreResults: boolean;
  handleGenerateQuery: () => Promise<void>;
  handleSearch: (queryOverride?: string) => Promise<void>;
  handleSearchMore: () => Promise<void>;
  handleButtonClick: () => void;
  handleGenerateAndSearch: () => Promise<void>;
  handleEmployeeChanged: (update: Partial<EmployeeDTO> & { id: string }) => void;
  getButtonText: () => string;
  isButtonDisabled: () => boolean;
  getDisabledTooltip: () => string | null;
};

/**
 * Shared hook for employee search logic used across desktop, mobile, and component views.
 */
export function useEmployeeSearch({
  company,
  smallCompanyDetection = { mode: "exact", threshold: SMALL_COMPANY_THRESHOLD },
}: UseEmployeeSearchOptions): UseEmployeeSearchReturn {
  const api = useApi();
  const toastError = useToastError();
  const { t } = useTranslation();
  const { teamUser, refreshTeamUser } = useUser();

  const [employees, setEmployees] = useState<EmployeeDTO[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isSearchQueryLoading, setIsSearchQueryLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isGeneratingQuery, setIsGeneratingQuery] = useState(false);
  const [targetMatch, setTargetMatch] = useState<TargetMatch | null>(null);

  const isSmallCompany =
    smallCompanyDetection.mode === "exact"
      ? company.employeesCount.exact !== null &&
        company.employeesCount.exact <=
          (smallCompanyDetection.threshold ?? SMALL_COMPANY_THRESHOLD)
      : company.employeesCount.estimatedRange === smallCompanyDetection.value;

  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState<string>("");

  const queryHasChanged = searchQuery !== lastSearchQuery;
  const isSearchMoreMode = currentPage >= 1 && hasSearched && !queryHasChanged;
  const maxPagesReached = currentPage >= MAX_PAGES && hasMoreResults;

  const fetchAllEmployees = useCallback(async () => {
    setIsSearching(true);
    setHasSearched(true);
    setError(null);

    try {
      const { data } = await api.get<EmployeeSearchResponse>(
        `/company/${company.id}/employees`,
      );

      setEmployees(data.employees);
      setHasMoreResults(false);
      setCurrentPage(1);
      setTargetMatch(data.targetMatch);
    } catch (err) {
      setEmployees([]);
      setError(err as Error);
    } finally {
      setIsSearching(false);
      setIsSearchQueryLoading(false);
    }
  }, [api, company.id]);

  const handleSearch = useCallback(
    async (queryOverride?: string) => {
      const query = queryOverride ?? searchQuery;
      if (!query.trim()) return;

      setIsSearching(true);
      setHasSearched(true);
      setError(null);
      setEmployees([]);
      setCurrentPage(1);
      setLastSearchQuery(query);

      try {
        const { data } = await api.get<EmployeeSearchResponse>(
          `/company/${company.id}/employees?query=${encodeURIComponent(query)}&page=1`,
        );
        await api.post("/my/team/me/custom-search-contacts-query", { query });
        await refreshTeamUser();

        setEmployees(data.employees);
        setHasMoreResults(data.hasMoreResults);
        setTargetMatch(data.targetMatch);
      } catch (err) {
        setEmployees([]);
        setError(err as Error);
        // A refusal (the daily reads, a throttle) is shown by the screen
        // holding the search, with its reason; anything else is toasted.
        if (!companyRefusalKey(err)) toastError();
      } finally {
        setIsSearching(false);
      }
    },
    [api, company.id, refreshTeamUser, searchQuery, toastError],
  );

  const generateQuery = useCallback(async (): Promise<string | null> => {
    const params = new URLSearchParams({ force: "true" });
    if (searchQuery.trim()) {
      params.append("previousQuery", searchQuery.trim());
    }
    const { data } = await api.get<string>(
      `/my/team/ai-suggestions/search-employees-query?${params.toString()}`,
    );
    setSearchQuery(data);
    return data;
  }, [api, searchQuery]);

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchQueryLoading(true);
      setError(null);

      if (isSmallCompany) {
        await fetchAllEmployees();
        return;
      }

      if (teamUser?.customSearchContactsQuery) {
        setSearchQuery(teamUser.customSearchContactsQuery);
        setIsSearchQueryLoading(false);
        handleSearch(teamUser.customSearchContactsQuery);
        return;
      }

      try {
        const { data } = await api.get<string>(
          `/my/team/ai-suggestions/search-employees-query`,
        );
        setSearchQuery(data);
        handleSearch(data);
      } catch {
        setSearchQuery("");
      } finally {
        setIsSearchQueryLoading(false);
      }
    };

    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateQuery = useCallback(async () => {
    setIsSearchQueryLoading(true);
    setIsGeneratingQuery(true);
    try {
      await generateQuery();
    } catch {
      // Keep existing query on error
    } finally {
      setIsGeneratingQuery(false);
      setIsSearchQueryLoading(false);
    }
  }, [generateQuery]);

  const handleSearchMore = useCallback(async () => {
    if (!searchQuery.trim() || !hasMoreResults || maxPagesReached) return;

    setIsSearching(true);
    setError(null);

    const nextPage = currentPage + 1;

    try {
      const { data } = await api.get<EmployeeSearchResponse>(
        `/company/${company.id}/employees?query=${encodeURIComponent(searchQuery)}&page=${nextPage}`,
      );

      const existingIds = new Set(employees.map((e) => e.id));
      const newEmployees = data.employees.filter(
        (e) => !existingIds.has(e.id),
      );
      setEmployees((prev) => [...prev, ...newEmployees]);

      setCurrentPage(nextPage);
      setHasMoreResults(data.hasMoreResults);
      setTargetMatch(data.targetMatch);

    } catch (err) {
      setError(err as Error);
      if (!companyRefusalKey(err)) toastError();
    } finally {
      setIsSearching(false);
    }
  }, [
    api,
    company.id,
    currentPage,
    employees,
    hasMoreResults,
    maxPagesReached,
    searchQuery,
    toastError,
  ]);

  const handleButtonClick = useCallback(() => {
    if (isSearchMoreMode) {
      handleSearchMore();
    } else {
      handleSearch();
    }
  }, [handleSearch, handleSearchMore, isSearchMoreMode]);

  const handleGenerateAndSearch = useCallback(async () => {
    setIsGeneratingQuery(true);
    setIsSearchQueryLoading(true);
    setError(null);

    try {
      const newQuery = await generateQuery();
      setIsSearchQueryLoading(false);
      setIsGeneratingQuery(false);

      if (newQuery) {
        await handleSearch(newQuery);
      }
    } catch (err) {
      setError(err as Error);
      setIsGeneratingQuery(false);
    }
  }, [generateQuery, handleSearch]);

  const handleEmployeeChanged = useCallback(
    (update: Partial<EmployeeDTO> & { id: string }) => {
      setEmployees((prev) =>
        prev.map((employee) =>
          employee.id === update.id ? { ...employee, ...update } : employee,
        ),
      );
    },
    [],
  );

  const getButtonText = useCallback((): string => {
    if (isSearchMoreMode) {
      return t("company.employees.searchQuery.searchMore");
    }
    return t("company.employees.searchQuery.search");
  }, [isSearchMoreMode, t]);

  const isButtonDisabled = useCallback((): boolean => {
    if (isSearching) return true;
    if (isGeneratingQuery || isSearchQueryLoading) return true;
    if (!searchQuery.trim()) return true;
    if (queryHasChanged) return false;
    if (maxPagesReached) return true;
    if (currentPage >= 1 && !hasMoreResults) return true;
    return false;
  }, [
    currentPage,
    hasMoreResults,
    isGeneratingQuery,
    isSearchQueryLoading,
    isSearching,
    maxPagesReached,
    queryHasChanged,
    searchQuery,
  ]);

  const getDisabledTooltip = useCallback((): string | null => {
    if (isSearching || !searchQuery.trim() || queryHasChanged) return null;
    if (maxPagesReached) {
      return t("company.employees.searchQuery.disabledTooltip.refineSearch");
    }
    if (currentPage >= 1 && !hasMoreResults) {
      return t("company.employees.searchQuery.disabledTooltip.noMoreResults");
    }
    return null;
  }, [
    currentPage,
    hasMoreResults,
    isSearching,
    maxPagesReached,
    queryHasChanged,
    searchQuery,
    t,
  ]);

  return {
    employees,
    error,
    searchQuery,
    setSearchQuery,
    isSearchQueryLoading,
    isSearching,
    hasSearched,
    isGeneratingQuery,
    targetMatch,
    isSmallCompany,
    queryHasChanged,
    isSearchMoreMode,
    maxPagesReached,
    currentPage,
    hasMoreResults,
    handleGenerateQuery,
    handleSearch,
    handleSearchMore,
    handleButtonClick,
    handleGenerateAndSearch,
    handleEmployeeChanged,
    getButtonText,
    isButtonDisabled,
    getDisabledTooltip,
  };
}
